import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import axiosInstance from '@/requests/axios'
import {
  submitCorporateDocuments,
  uploadCorporateDocuments,
} from '@/requests/corporate'
import clsx from 'clsx'

interface ValidationError {
  file?: string
}

export default function CorporateArticleOfIncorporationUpload() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const certificateObjectName = useCorpStore(
    (state) => state.certificateObjectName
  )
  const incorporationIssuer = useCorpStore((state) => state.incorporationIssuer)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [errors, setErrors] = useState<ValidationError>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isValidFileType = (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png']
    return allowedTypes.includes(file.type)
  }

  const validateFile = (file: File | null): string | undefined => {
    if (!file) {
      return t('corporateDocuments.errors.fileRequired')
    }
    if (!isValidFileType(file)) {
      return t('corporateDocuments.errors.invalidFileType')
    }
    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      return t('corporateDocuments.errors.fileSizeExceeded')
    }
    return undefined
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    setTouched((prev) => ({ ...prev, file: true }))

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      const error = validateFile(droppedFile)
      if (error) {
        setErrors({ file: error })
      } else {
        setFile(droppedFile)
        setErrors({})
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    setTouched((prev) => ({ ...prev, file: true }))

    if (selectedFile) {
      const error = validateFile(selectedFile)
      if (error) {
        setErrors({ file: error })
      } else {
        setFile(selectedFile)
        setErrors({})
      }
    }
  }

  useEffect(() => {
    const handleDocumentKeyDown = async (e: KeyboardEvent) => {
      const isFormComplete = file && !isSubmitting

      if (e.key === 'Enter' && isFormComplete) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [file, isSubmitting])

  const handleSubmit = async () => {
    if (isSubmitting || !file) return

    setIsSubmitting(true)

    setTouched({ file: true })
    const error = validateFile(file)

    if (error) {
      setErrors({ file: error })
      setIsSubmitting(false)
      return
    }

    try {
      if (!file) {
        setErrors({
          file: t('corporateDocuments.errors.invalidArticles'),
        })
        setIsSubmitting(false)
        return
      }

      if (!certificateObjectName) {
        setErrors({
          file: t('corporateDocuments.errors.previousDocumentNotFound'),
        })
        setIsSubmitting(false)
        return
      }

      const [articlesObjectName] = await uploadCorporateDocuments([
        { file, name: file.name, contentType: file.type },
      ])

      const response = await submitCorporateDocuments({
        objectNames: [certificateObjectName, articlesObjectName],
        incorporationJurisdiction: incorporationIssuer,
      })

      if (response.status === 200) {
        nextState()
        navigate(ROUTES.CORPORATE_ASSIST)
      } else {
        setErrors({
          file: 'ERROR',
        })
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Error uploading documents:', error)
      setErrors({
        file: t('corporateDocuments.errors.submitError'),
      })
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="container-corporate-documents"
    >
      <div
        className="text-lg font-normal uppercase"
        data-testid="text-corporate-documents-heading"
      >
        {t('corporateDocuments.title')}
      </div>

      <div
        className="flex flex-col gap-2"
        data-testid="section-corporate-documents-section"
      >
        <p
          className="text-base"
          data-testid="text-corporate-documents-section-heading"
        >
          {t('corporateDocuments.sectionHeading')}
        </p>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-8
            flex flex-col items-center justify-center
            cursor-pointer transition-all
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
            ${file ? 'bg-green-50' : 'hover:bg-gray-50'}
            ${touched.file && errors.file ? 'border-red-500' : ''}
          `}
          data-testid="container-corporate-documents-upload"
          onClick={() => document.getElementById('file-upload')?.click()}
        >
          <input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png"
            data-testid="input-corporate-documents-file"
          />
          {file ? (
            <div
              className="flex flex-col items-center"
              data-testid="container-corporate-documents-selected-file"
            >
              <p
                className="text-green-600 font-medium"
                data-testid="text-corporate-documents-filename"
              >
                {file.name}
              </p>
              <p
                className="text-sm text-gray-500 mt-1"
                data-testid="text-corporate-documents-change-prompt"
              >
                {t('corporateDocuments.upload.changePrompt')}
              </p>
            </div>
          ) : (
            <div
              className="flex flex-col items-center"
              data-testid="text-corporate-documents-upload-prompt"
            >
              <p
                className="font-medium"
                data-testid="text-corporate-documents-upload-heading"
              >
                {t('corporateDocuments.upload.heading')}
              </p>
              <p
                className="text-sm text-gray-500 mt-1"
                data-testid="text-corporate-documents-upload-instructions"
              >
                {t('corporateDocuments.upload.instructions')}
              </p>
              <p
                className="text-xs text-gray-400 mt-1"
                data-testid="text-corporate-documents-upload-format-info"
              >
                {t('corporateDocuments.upload.formatInfo')}
              </p>
            </div>
          )}
        </div>
        {touched.file && errors.file && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-documents-file"
          >
            {errors.file}
          </span>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !file}
        className={clsx(
          'mt-4 inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
          {
            'opacity-50 cursor-not-allowed bg-gray-400': !file || isSubmitting,
            'hover:bg-gray-500': !file || isSubmitting,
          }
        )}
        data-testid="btn-corporate-documents-submit"
      >
        {isSubmitting
          ? t('corporateDocuments.submitButton.processing')
          : t('corporateDocuments.submitButton.continue')}
      </Button>
    </div>
  )
}

/*
Component Improvement Suggestions:
- Add proper ARIA attributes for enhanced accessibility
- Add support for screen readers to announce errors and upload status
- Implement visual progress indicators for file upload process
- Add file size validation before full upload to improve user experience
- Consider adding a "Remove" button next to the selected file instead of requiring a new selection
- Display file size for selected files to help users understand upload limitations
- Add a confirmation step before submitting large files
- Improve error handling with more specific error messages
- Implement retry logic for failed uploads
- Consider adding a preview feature for uploaded files when possible
- Add proper focus management for keyboard navigation
- Include appropriate loading states during file processing
- Consider implementing chunked uploads for larger files
- Add better mobile support for drag and drop functionality
*/
